import { useState, useEffect, useCallback } from 'react'
import { AlertTriangle, RefreshCw, X } from 'lucide-react'
import { monitoringApi } from '../services/api'
import { useAuth } from '../context/AuthContext'

// Detects a WEDGED central Asterisk: a server with active bots that was clearly working
// in the last 24h but has taken ZERO calls in the last 20 min (whole host went dark =
// frozen chan_sip registration engine). Shows a red banner with a one-click "Restart
// Asterisk" recovery per host. Polls every 60s. Admin-only.
export default function WedgedHostBanner() {
  const { isAdmin } = useAuth()
  const [hosts, setHosts] = useState([])
  const [dismissed, setDismissed] = useState('')
  const [busy, setBusy] = useState({})        // { [serverId]: true } while restarting
  const [done, setDone] = useState({})        // { [serverId]: 'ok' | 'fail' }

  const poll = useCallback(async () => {
    try {
      const { data } = await monitoringApi.getHostHealth()
      setHosts(data?.dark_hosts || [])
    } catch (e) { /* ignore transient errors */ }
  }, [])

  useEffect(() => {
    if (!isAdmin) return
    let stop = false
    const run = async () => { if (!stop) await poll() }
    run()
    const id = setInterval(run, 60000)
    return () => { stop = true; clearInterval(id) }
  }, [isAdmin, poll])

  const restart = async (h) => {
    if (!window.confirm(`Restart the central Asterisk on ${h.name} (${h.host_ip})?\n\nThis re-registers every bot on that host to its dialler and briefly affects ALL bots on the host. Use it when the host has gone dark (registration wedge).`)) return
    setBusy(b => ({ ...b, [h.id]: true }))
    setDone(d => { const n = { ...d }; delete n[h.id]; return n })
    try {
      const { data } = await monitoringApi.restartAsterisk(h.id)
      setDone(d => ({ ...d, [h.id]: data?.success ? 'ok' : 'fail' }))
      if (data?.success) setTimeout(poll, 5000) // re-check; should clear once calls resume
    } catch (e) {
      setDone(d => ({ ...d, [h.id]: 'fail' }))
    } finally {
      setBusy(b => { const n = { ...b }; delete n[h.id]; return n })
    }
  }

  if (!isAdmin || hosts.length === 0) return null
  const key = hosts.map(h => h.id).sort().join(',')
  if (dismissed === key) return null

  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 10, margin: '0 0 14px',
      padding: '12px 14px', borderRadius: 10,
      background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.4)', color: '#fca5a5'
    }}>
      <AlertTriangle size={18} style={{ flexShrink: 0, marginTop: 1 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 700, color: '#ef4444' }}>
          {hosts.length} server{hosts.length === 1 ? '' : 's'} went dark — central Asterisk likely wedged (no calls landing)
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
          {hosts.map(h => (
            <div key={h.id} style={{
              display: 'flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap',
              background: 'rgba(0,0,0,0.15)', borderRadius: 8, padding: '5px 10px', fontSize: 12.5
            }}>
              <strong style={{ fontFamily: 'monospace' }}>{h.name}</strong>
              <span style={{ opacity: 0.7 }}>{h.host_ip} · {h.active_bots} bots · 0 calls/20m</span>
              {done[h.id] === 'ok' ? (
                <span style={{ color: '#22c55e', fontWeight: 600 }}>✓ restarted</span>
              ) : done[h.id] === 'fail' ? (
                <span style={{ color: '#ef4444', fontWeight: 600 }}>✗ failed — retry</span>
              ) : null}
              <button
                onClick={() => restart(h)}
                disabled={!!busy[h.id]}
                title="Restart this host's central Asterisk"
                style={{
                  display: 'flex', alignItems: 'center', gap: 5, cursor: busy[h.id] ? 'wait' : 'pointer',
                  background: '#ef4444', color: '#fff', border: 'none', borderRadius: 6,
                  padding: '4px 10px', fontSize: 12, fontWeight: 600
                }}>
                <RefreshCw size={12} className={busy[h.id] ? 'spin' : ''} />
                {busy[h.id] ? 'Restarting…' : 'Restart Asterisk'}
              </button>
            </div>
          ))}
        </div>
        <div style={{ fontSize: 11, opacity: 0.7, marginTop: 6 }}>
          Active bots but zero calls for 20+ min = the host's SIP registrations froze. Restarting
          re-registers every bot on the host to its dialler. Verify a real cause before clicking (e.g. not just a paused client).
        </div>
      </div>
      <button onClick={() => setDismissed(key)} title="Dismiss until the set changes"
        style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#fca5a5', flexShrink: 0 }}>
        <X size={16} />
      </button>
    </div>
  )
}
